export const colors = {
  primary: "#11017A",
  secondary: "#F1EEFF",
  white: "#FFFFFF",
  black: "#000000",
  lightGray: "#d3d3d3",
  darkGray: "#8C8E90",
  red: "#D24040",
  lightRed: "#FFD3D3",
  darkYellow: "#d4b106",
  yellow: "#fffb8f",
  green: "#147129",
  lightGreen: "#EAFDEE",
  darkBlue: "#03045E",
  calendar: [
    { bg: "#E3F2FD", text: "#0B5394" }, // light blue
    { bg: "#FFF3E0", text: "#7A4900" }, // light orange
    { bg: "#E8F5E9", text: "#1B5E20" }, // light green
    { bg: "#F3E5F5", text: "#4A148C" }, // light purple
    { bg: "#FCE4EC", text: "#880E4F" }, // light pink
    { bg: "#E0F7FA", text: "#006064" }, // light cyan
    { bg: "#FFFDE7", text: "#827717" }, // light yellow
    { bg: "#EDE7F6", text: "#311B92" }, // light deep purple
    { bg: "#F1F8E9", text: "#33691E" }, // pale lime/green
    { bg: "#FFF8E1", text: "#795548" }, // cream / brown text
    { bg: "#E0F2F1", text: "#004D40" }, // mint / teal text
    { bg: "#FBE9E7", text: "#BF360C" }, // peach / burnt orange text
    { bg: "#E8EAF6", text: "#1A237E" }, // indigo wash
    { bg: "#E1F5FE", text: "#01579B" }, // sky blue
    { bg: "#F9FBE7", text: "#827717" }, // pale yellow-green
    { bg: "#FFF0F6", text: "#AD1457" }, // blush / magenta text
  ],

  // ── Spott design system semantic tokens (additive — see
  // 2026-05-26-terasku-design-system.md). Existing keys above are kept for
  // pages that still reference them; they will be migrated per page later. ──

  // Ink (primary action color + text)
  ink: "#0F0F0E",
  ink2: "#5B5B57",
  ink3: "#9A9A95",
  // One step lighter on the same warm-grey ladder — a neutral CHART fill
  // (private-class series next to navy). Light enough to stay quiet, but far
  // above the near-invisible bgSoftHover the charts originally used.
  ink4: "#B1B0AB",

  // Surfaces
  page: "#FAFAF7",
  pageOuter: "#EFEEE9",
  bgSoft: "#F2F1ED",
  bgSoftHover: "#EAE9E3", // hover/active tint of bgSoft (selected-state hovers)
  card: "#FFFFFF",

  // Navy (accent only — NOT for primary buttons)
  navy: "#2E3F8B",
  navyDark: "#1F2D6A",
  navyTint: "#E4E8F6",

  // Amber — two-tier semantic
  amber: "#EF9F27",
  amberDark: "#BA7517",
  amberTint: "#FBEEDA",
  amberDeep: "#C75817",

  // Status
  success: "#2E8B5E",
  successDark: "#1F6B43", // readable green text on successTint (badges)
  successTint: "#DDEEE3",
  error: "#C84A3F",
  errorTint: "#F6DDD8", // error text on a soft red tint (badges)
  purple: "#534689", // Waitlisted status + the Reward / Beauty label chips (same hue as the lilac instructor tint)
  purpleTint: "#EAE6F4",
  // The calendarWarm rose, kept as a plain accent (Tambah Klien icon circle).
  // Badges no longer use it: the six badge tones are green / navy / amber /
  // grey / red / purple (see the status-badge rule in shared/utils).
  pink: "#9A3B4E",

  // Borders (translucent ink)
  line: "rgba(15, 15, 14, 0.07)",
  lineStrong: "rgba(15, 15, 14, 0.14)",
  lineInput: "rgba(15, 15, 14, 0.18)", // darker resting border for form controls

  // A class taught by someone who is no longer an active instructor
  // (deactivated, or their instructor role removed): a neutral warm grey with
  // no hue, so it can't be read as any instructor's colour.
  calendarInactive: {
    bg: "#E6E5E0",
    bgPast: "#EFEEEA",
    accent: "#9A9A95",
    text: "#5B5B57",
    border: "rgba(15, 15, 14, 0.16)",
  },
};

// An instructor's colour on the calendar, the list view and payroll. `bg` is
// the block fill, `bgPast` the fill once the class has ended (text keeps its
// colour, so it stays readable), `accent` the bar on the block's left edge and
// the dot in pickers and filters, `text` the block's text, `border` a soft
// outline for the pop-up blocks. Every `text` is at least 9:1 on its `bg` and
// `bgPast`, every `accent` at least 3:1 on its `bg`, and no two accents are
// closer than ΔE2000 13 (checked by script when the palette was made).
export type CalendarColor = {
  bg: string;
  bgPast: string;
  accent: string;
  text: string;
  border: string;
};

// The 14 instructor colours in display order (around the colour wheel), keyed
// by the value the server stores on `staff.calendar_color`. The keys are
// mirrored in terasku-server/src/util/calendar-color.ts, which also decides the
// order new instructors get them in (most distinct first); keep the two in
// sync, the same way as role.ts.
export const CALENDAR_COLOR_KEYS = [
  "navy",
  "blue",
  "cyan",
  "teal",
  "green",
  "moss",
  "olive",
  "gold",
  "orange",
  "brick",
  "rose",
  "plum",
  "violet",
  "taupe",
] as const;

export type CalendarColorKey = (typeof CALENDAR_COLOR_KEYS)[number];

export const calendarPalette: Record<CalendarColorKey, CalendarColor> = {
  navy: { bg: "#E5EDFE", bgPast: "#F5F8FE", accent: "#405EAD", text: "#243971", border: "rgba(64, 94, 173, 0.22)" },
  blue: { bg: "#E8F5FE", bgPast: "#F2F9FE", accent: "#248FCC", text: "#014263", border: "rgba(36, 143, 204, 0.22)" },
  cyan: { bg: "#CDF5FF", bgPast: "#EDFBFF", accent: "#086F81", text: "#034551", border: "rgba(8, 111, 129, 0.22)" },
  teal: { bg: "#D1FDF8", bgPast: "#EDFCFA", accent: "#0C9A92", text: "#014743", border: "rgba(12, 154, 146, 0.22)" },
  green: { bg: "#D5F7E2", bgPast: "#F0FCF4", accent: "#037649", text: "#004A2B", border: "rgba(3, 118, 73, 0.22)" },
  moss: { bg: "#E9F9DA", bgPast: "#F5FAEF", accent: "#6C9539", text: "#2C4601", border: "rgba(108, 149, 57, 0.22)" },
  olive: { bg: "#F0EFCD", bgPast: "#F9F9ED", accent: "#6C6602", text: "#433F01", border: "rgba(108, 102, 2, 0.22)" },
  gold: { bg: "#FEF1D9", bgPast: "#FEF7EC", accent: "#AC7D08", text: "#503800", border: "rgba(172, 125, 8, 0.22)" },
  orange: { bg: "#FFE8D8", bgPast: "#FFF6F0", accent: "#944F04", text: "#5D2F00", border: "rgba(148, 79, 4, 0.22)" },
  brick: { bg: "#FEEFEC", bgPast: "#FFF6F4", accent: "#C86556", text: "#68231B", border: "rgba(200, 101, 86, 0.22)" },
  rose: { bg: "#FFE5EB", bgPast: "#FEF5F7", accent: "#9C3D5D", text: "#652139", border: "rgba(156, 61, 93, 0.22)" },
  plum: { bg: "#FFECFA", bgPast: "#FEF5FC", accent: "#B466A6", text: "#5B2552", border: "rgba(180, 102, 166, 0.22)" },
  violet: { bg: "#F4F0FE", bgPast: "#F9F7FE", accent: "#9073CA", text: "#442F6A", border: "rgba(144, 115, 202, 0.22)" },
  taupe: { bg: "#F4EBE3", bgPast: "#FBF7F5", accent: "#745F4A", text: "#493A2B", border: "rgba(116, 95, 74, 0.22)" },
};

// The swatch names on the instructor profile's colour picker: one English word
// per colour in both languages (Andrew, 2026-10-03), like the label chips.
export const CALENDAR_COLOR_LABELS: Record<CalendarColorKey, string> = {
  navy: "Navy",
  blue: "Blue",
  cyan: "Cyan",
  teal: "Teal",
  green: "Green",
  moss: "Moss",
  olive: "Olive",
  gold: "Gold",
  orange: "Orange",
  brick: "Brick",
  rose: "Rose",
  plum: "Plum",
  violet: "Violet",
  taupe: "Taupe",
};

/**
 * Tinted icon chips: the soft square behind an icon on a hub row, a dashboard
 * metric card or a client's package card. One hue per group, so "the green
 * ones" is findable without reading.
 *
 * Deliberately NOT named `Tone`: `StatusTone` in shared/utils is a different
 * set for status badges, and the two unions share `navy`, `neutral` and
 * `purple`, so a bare `Tone` beside it invites importing the wrong one in a
 * way that would still type-check.
 *
 * No red: `error` reads as a failure state everywhere else in the app.
 */
export type IconTone = "neutral" | "navy" | "amber" | "green" | "purple";

export const ICON_TONES: Record<IconTone, { bg: string; fg: string }> = {
  neutral: { bg: colors.bgSoft, fg: colors.ink2 },
  navy: { bg: colors.navyTint, fg: colors.navy },
  amber: { bg: colors.amberTint, fg: colors.amberDark },
  // Dark variants where the base hue is too light to read on its own tint.
  green: { bg: colors.successTint, fg: colors.successDark },
  purple: { bg: colors.purpleTint, fg: colors.purple },
};
